import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject, Subscription, distinctUntilChanged, filter, firstValueFrom, map, take } from 'rxjs';
import * as Base64url from 'crypto-js/enc-base64url';
import * as Utf8 from 'crypto-js/enc-utf8';
import { AppMetadataStore } from '../../baseServices/app-metadata-store.service';
import { telemetryUuid } from '../../baseServices/logging/telemetry-identity.service';
import { GlobalConstants } from '../../constant/constant';
import { AppStateService } from '../app-state.service';

export interface SessionIdentity {
  readonly userId: number;
  readonly loginContextId?: string;
}

export function sameSessionIdentity(first?: SessionIdentity, second?: SessionIdentity): boolean {
  return first?.userId === second?.userId && first?.loginContextId === second?.loginContextId;
}

export interface AuthSessionState {
  readonly phase: 'initializing' | 'authenticated' | 'anonymous';
  readonly reason: 'startup' | 'restored' | 'signed-in' | 'replaced' | 'ended' | 'expired' | 'invalid' | 'unavailable';
  readonly identity?: SessionIdentity;
  readonly expiresAt?: number;
}

interface SessionUser {
  Id: number;
  Email: string;
  AccessToken: string;
  LoginContextId?: string;
  SessionExpiresAt?: unknown;
  IsActive?: boolean;
  RoleClaims?: unknown;
  OfflinePasswordRecord?: string | null;
}

/** Owns session persistence, restoration, replacement and expiry; publishes no credentials and performs no HTTP, logging or navigation. */
@Injectable({ providedIn: 'root' })
export class AuthSessionService implements OnDestroy {
  private readonly maxTimerDelayMs = 2147483647;
  private readonly stateSubject = new BehaviorSubject<AuthSessionState>(Object.freeze({ phase: 'initializing', reason: 'startup' }));
  readonly state$ = this.stateSubject.asObservable();
  readonly identity$ = this.state$.pipe(map(state => state.identity), distinctUntilChanged(sameSessionIdentity));
  private readonly signInSubject = new Subject<SessionIdentity>();
  /** A fresh sign-in occurrence, not replayed to late subscribers or emitted during restoration. */
  readonly signInCompleted$ = this.signInSubject.asObservable();
  private completedLoginContext?: string;
  get state(): AuthSessionState { return this.stateSubject.value; }
  readonly ready: Promise<void>;
  private expiryTimer?: ReturnType<typeof setTimeout>;
  get timeoutInterval(): ReturnType<typeof setTimeout> | undefined { return this.expiryTimer; }
  private readonly appStateSubscription: Subscription;
  private revision = 0;
  private destroyed = false;
  private restoreOnForeground = false;
  private readonly controllers = new Set<AbortController>();
  private readonly storageListener = (event: StorageEvent) => {
    if (event.key !== GlobalConstants.USER_KEY && event.key !== null) { return; }
    try {
      const saved = this.readUser();
      if (!saved || !this.matches(this.state.identity, saved)) {
        this.publish({ phase: 'initializing', reason: 'replaced' });
      }
    } catch {
      this.publishUnavailable();
    }
    void this.restore();
  };
  private readonly foregroundListener = () => {
    if (document.visibilityState === 'visible') { this.reconcileForeground(); }
  };

  constructor(private metadata: AppMetadataStore, appState: AppStateService, private ngZone: NgZone) {
    this.ready = firstValueFrom(this.state$.pipe(
      filter(state => state.phase !== 'initializing'), take(1), map(() => undefined)));
    window.addEventListener('storage', this.storageListener);
    document.addEventListener('visibilitychange', this.foregroundListener);
    window.addEventListener('focus', this.foregroundListener);
    this.appStateSubscription = appState.getAppState().subscribe(state => {
      if (state.isActive) { this.reconcileForeground(); }
    });
    void this.restore();
  }

  ngOnDestroy(): void {
    if (this.destroyed) { return; }
    this.destroyed = true;
    this.revision++;
    this.controllers.forEach(controller => controller.abort());
    this.appStateSubscription.unsubscribe();
    window.removeEventListener('storage', this.storageListener);
    document.removeEventListener('visibilitychange', this.foregroundListener);
    window.removeEventListener('focus', this.foregroundListener);
    this.publish({ phase: 'anonymous', reason: 'ended' });
    this.stateSubject.complete();
    this.signInSubject.complete();
  }

  /** Atomically replaces credentials and their offline artifact; failed replacement rechecks the persisted session, never a cached state. */
  async signIn(user: SessionUser): Promise<SessionIdentity | undefined> {
    const revision = ++this.revision;
    let failureReason: AuthSessionState['reason'] = 'invalid';
    try {
      if (!this.validUser(user)) { throw new Error('Authentication response has incomplete credentials'); }
      const expiresAt = this.tokenExpiresAt(user.AccessToken);
      if (expiresAt === undefined || expiresAt <= Date.now()) {
        throw new Error('Authentication response has an invalid or expired token');
      }
      failureReason = 'unavailable';
      return await this.executeWithLock(() => {
        if (this.destroyed || revision !== this.revision) { return undefined; }
        if (expiresAt <= Date.now()) { throw new Error('Authentication token expired while waiting for persistence'); }
        const saved: SessionUser = {
          Id: user.Id, Email: user.Email, AccessToken: user.AccessToken,
          IsActive: user.IsActive, RoleClaims: user.RoleClaims,
          LoginContextId: telemetryUuid(), OfflinePasswordRecord: null,
        };
        this.writeUser(saved);
        try { this.removeLegacyCredentials(); } catch {}
        this.publishUser(saved, 'signed-in');
        return this.state.identity;
      });
    } catch (error) {
      if (!this.destroyed && revision === this.revision) {
        try {
          const saved = this.readUser();
          if (saved && this.normalizeContext(saved.LoginContextId)) {
            this.publishUser(saved, this.matches(this.state.identity, saved) ? this.state.reason : 'replaced');
          } else if (saved) {
            this.publishUnavailable();
          } else {
            this.publish({ phase: 'anonymous', reason: failureReason });
          }
        } catch {
          this.publishUnavailable();
        }
      }
      throw error;
    }
  }

  isAuthenticated(): boolean {
    return !!this.reconcileExpiration();
  }

  /** Returns credentials only for an active canonical session, optionally restricted to an originating identity. */
  getAccessToken(identity?: SessionIdentity): string | undefined {
    const saved = this.reconcileExpiration();
    if (!saved || (identity && !this.matches(identity, saved))) { return undefined; }
    return saved.AccessToken;
  }

  completeSignIn(identity: SessionIdentity): boolean {
    if (this.destroyed || this.state.reason !== 'signed-in') { return false; }
    const saved = this.reconcileExpiration();
    if (!saved || !this.matches(identity, saved)) { return false; }
    if (this.completedLoginContext === identity.loginContextId) { return false; }
    this.completedLoginContext = identity.loginContextId;
    this.ngZone.run(() => this.signInSubject.next(identity));
    return true;
  }

  /** Compares session generations; this does not establish token validity or expiration eligibility. */
  isCurrent(identity: SessionIdentity): boolean {
    try {
      const saved = this.readUser();
      return !!saved && this.matches(this.state.identity, saved) && this.matches(identity, saved);
    } catch { return false; }
  }

  async storeOfflinePassword(identity: SessionIdentity, record: string): Promise<void> {
    await this.executeWithLock(() => {
      const saved = this.reconcileExpiration();
      if (saved && this.matches(identity, saved)) {
        this.writeUser({ ...saved, OfflinePasswordRecord: record });
        try { localStorage.removeItem(GlobalConstants.OFFLINE_PASSWORD_RECORD); } catch {}
      }
    });
  }

  /** Reads only the active canonical session's offline artifact; retired/replaced sessions cannot use a retained legacy hash. */
  getOfflinePasswordRecord(identity?: SessionIdentity): string | undefined {
    const saved = this.reconcileExpiration();
    if (!saved || (identity && !this.matches(identity, saved))) { return undefined; }
    return typeof saved.OfflinePasswordRecord === 'string' ? saved.OfflinePasswordRecord : undefined;
  }

  async clearOfflinePassword(identity = this.state.identity): Promise<void> {
    await this.executeWithLock(() => {
      const saved = this.reconcileExpiration();
      if (saved && identity && this.matches(identity, saved)) {
        this.writeUser({ ...saved, OfflinePasswordRecord: null });
        try { localStorage.removeItem(GlobalConstants.OFFLINE_PASSWORD_RECORD); } catch {}
      }
    });
  }

  /**
   * Retires the originating session locally immediately. During initialization, unknown persisted credentials are restored, not deleted.
   * False means shared cleanup failed, or the originating identity/token was unknown or superseded; callers needing startup logout must await ready.
   */
  async end(reason: 'ended' | 'expired' | 'invalid' = 'ended', rejectedAccessToken?: string): Promise<boolean> {
    if (this.destroyed) { return false; }
    const identity = this.state.identity;
    const revision = ++this.revision;
    this.restoreOnForeground = false;
    this.publish({ phase: 'initializing', reason });
    try {
      return await this.executeWithLock(() => {
        if (this.destroyed || revision !== this.revision) { return false; }
        const saved = this.readUser();
        const replaced = saved && (!this.matches(identity, saved) ||
          (rejectedAccessToken !== undefined && saved.AccessToken !== rejectedAccessToken));
        if (replaced) {
          this.publish({ phase: 'initializing', reason: 'replaced' });
          this.restoreSaved(true);
          return false;
        }
        if (saved && reason === 'expired') {
          const expiresAt = this.tokenExpiresAt(saved.AccessToken);
          if (expiresAt !== undefined && expiresAt > Date.now()) {
            this.restoreSaved(true);
            return false;
          }
        }
        this.removeCredentials();
        this.publish({ phase: 'anonymous', reason });
        return true;
      });
    } catch {
      if (!this.destroyed && revision === this.revision) {
        this.publishUnavailable('none');
      }
      return false;
    }
  }

  private async restore(): Promise<void> {
    const revision = ++this.revision;
    try {
      await this.executeWithLock(() => {
        if (!this.destroyed && revision === this.revision) { this.restoreSaved(true); }
      });
    } catch {
      if (!this.destroyed && revision === this.revision) {
        try { this.restoreSaved(false); }
        catch { this.publishUnavailable(); }
      }
    }
  }

  private restoreSaved(coordinated: boolean): void {
    const raw = localStorage.getItem(GlobalConstants.USER_KEY);
    if (raw === null) {
      this.publish({ phase: 'anonymous', reason: this.state.reason === 'replaced' ? 'ended' : 'startup' });
      if (coordinated) { this.removeCredentials(); }
      return;
    }
    const user = this.readUser(raw);
    const expiresAt = user ? this.tokenExpiresAt(user.AccessToken) : undefined;
    if (!user || expiresAt === undefined || expiresAt <= Date.now()) {
      this.publish({ phase: 'anonymous', reason: expiresAt !== undefined ? 'expired' : 'invalid' });
      if (coordinated) { this.removeCredentials(); }
      return;
    }
    const reason = this.state.reason === 'replaced' ? 'replaced' : 'restored';
    const legacyPassword = this.readLegacyOfflinePassword(user);
    if (legacyPassword !== null) {
      this.publish({ phase: 'anonymous', reason: 'invalid' });
      if (coordinated) { this.removeCredentials(); }
      return;
    }
    if (coordinated && !this.normalizeContext(user.LoginContextId)) {
      const migrated = {
        ...user,
        LoginContextId: this.normalizeContext(user.LoginContextId) || telemetryUuid(),
        OfflinePasswordRecord: user.OfflinePasswordRecord ?? null,
      };
      try {
        this.writeUser(migrated);
      } catch {
        this.publishUnavailable();
        return;
      }
      try { this.removeLegacyCredentials(); } catch {}
      this.publishUser(migrated, reason);
      return;
    }
    if (!this.normalizeContext(user.LoginContextId) || legacyPassword !== null) {
      this.publishUnavailable();
      return;
    }
    this.publishUser(user, reason);
  }

  private readUser(raw = localStorage.getItem(GlobalConstants.USER_KEY)): SessionUser | undefined {
    if (raw === null) { return undefined; }
    try {
      const user: unknown = JSON.parse(raw);
      if (!this.validUser(user)) { return undefined; }
      if (user.OfflinePasswordRecord !== undefined && user.OfflinePasswordRecord !== null && typeof user.OfflinePasswordRecord !== 'string') {
        return undefined;
      }
      return user;
    } catch { return undefined; }
  }

  private validUser(user: unknown): user is SessionUser {
    if (!user || typeof user !== 'object') { return false; }
    const candidate = user as Record<string, unknown>;
    return typeof candidate.Id === 'number' && Number.isInteger(candidate.Id) && candidate.Id > 0 &&
      typeof candidate.Email === 'string' && !!candidate.Email &&
      typeof candidate.AccessToken === 'string' && !!candidate.AccessToken;
  }

  /** Reads JWT NumericDate claims for local scheduling only; the server verifies the signature and authorization. */
  private tokenExpiresAt(accessToken: string): number | undefined {
    try {
      const parts = accessToken.split('.');
      if (parts.length !== 3 || parts.some(part => !/^[a-zA-Z0-9_-]+$/.test(part))) { return undefined; }
      const payload: unknown = JSON.parse(Utf8.stringify(Base64url.parse(parts[1])));
      if (!payload || typeof payload !== 'object' || Array.isArray(payload)) { return undefined; }
      const expiration = (payload as Record<string, unknown>).exp;
      if (typeof expiration !== 'number' || !Number.isFinite(expiration)) { return undefined; }
      const expiresAt = expiration * 1000;
      return Number.isFinite(expiresAt) && Math.abs(expiresAt) <= 8640000000000000 ? expiresAt : undefined;
    } catch { return undefined; }
  }

  private normalizeContext(value: unknown): string | undefined {
    return typeof value === 'string' && /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(value)
      ? value.toLowerCase() : undefined;
  }

  private matches(identity: SessionIdentity | undefined, user: SessionUser): boolean {
    return !!identity && identity.userId === user.Id && identity.loginContextId === this.normalizeContext(user.LoginContextId);
  }

  private reconcileForeground(): void {
    if (this.destroyed) { return; }
    if (this.state.phase === 'anonymous' && this.state.reason === 'unavailable' && this.restoreOnForeground) {
      this.restoreOnForeground = false;
      this.publish({ phase: 'initializing', reason: 'restored' });
      void this.restore();
      return;
    }
    this.reconcileExpiration();
  }

  private publishUnavailable(recovery: 'foreground' | 'none' = 'foreground'): void {
    this.restoreOnForeground = recovery === 'foreground';
    this.publish({ phase: 'anonymous', reason: 'unavailable' });
  }

  private reconcileExpiration(): SessionUser | undefined {
    if (this.destroyed || this.state.phase !== 'authenticated') { return undefined; }
    try {
      const saved = this.readUser();
      if (!saved || !this.matches(this.state.identity, saved)) {
        this.publish({ phase: 'initializing', reason: 'replaced' });
        void this.restore();
        return undefined;
      }
      const expiresAt = this.tokenExpiresAt(saved.AccessToken);
      if (expiresAt === undefined) { void this.end('invalid', saved.AccessToken); return undefined; }
      if (expiresAt <= Date.now()) { void this.end('expired'); return undefined; }
      if (this.state.expiresAt !== expiresAt) { this.publishUser(saved, this.state.reason); }
      return this.state.phase === 'authenticated' ? saved : undefined;
    } catch {
      this.publishUnavailable();
      return undefined;
    }
  }

  private publishUser(user: SessionUser, reason: AuthSessionState['reason']): void {
    if (!this.normalizeContext(user.LoginContextId) || this.readLegacyOfflinePassword(user) !== null) {
      this.publishUnavailable();
      return;
    }
    const identity = Object.freeze({ userId: user.Id, loginContextId: this.normalizeContext(user.LoginContextId) });
    const expiresAt = this.tokenExpiresAt(user.AccessToken);
    if (expiresAt === undefined || expiresAt <= Date.now()) {
      this.publish({ phase: 'anonymous', reason: expiresAt !== undefined ? 'expired' : 'invalid' });
      return;
    }
    if (this.matches(this.state.identity, user) && this.state.expiresAt === expiresAt) { return; }
    this.publish({ phase: 'authenticated', reason, identity, expiresAt });
  }

  private publish(state: AuthSessionState): void {
    if (state.phase === 'authenticated') { this.restoreOnForeground = false; }
    clearTimeout(this.expiryTimer);
    this.expiryTimer = undefined;
    const published = Object.freeze(state);
    this.ngZone.run(() => this.stateSubject.next(published));
    if (!this.destroyed && this.state === published && state.phase === 'authenticated') {
      this.scheduleExpiration(published);
    }
  }

  private scheduleExpiration(state: AuthSessionState): void {
    if (this.destroyed || this.state !== state || state.expiresAt === undefined) { return; }
    clearTimeout(this.expiryTimer);
    const remaining = state.expiresAt - Date.now();
    this.expiryTimer = setTimeout(() => {
      if (this.state !== state) { return; }
      if (this.reconcileExpiration()) { this.scheduleExpiration(this.state); }
    }, Math.min(this.maxTimerDelayMs, Math.max(0, remaining)));
  }

  private removeCredentials(): void {
    localStorage.removeItem(GlobalConstants.USER_KEY);
    if (localStorage.getItem(GlobalConstants.USER_KEY) !== null) { throw new Error('Authentication session could not be removed'); }
    this.removeLegacyCredentials();
  }

  private writeUser(user: SessionUser): void {
    const serialized = JSON.stringify({
      Id: user.Id,
      Email: user.Email,
      AccessToken: user.AccessToken,
      IsActive: user.IsActive,
      RoleClaims: user.RoleClaims,
      LoginContextId: this.normalizeContext(user.LoginContextId),
      OfflinePasswordRecord: user.OfflinePasswordRecord ?? null,
    });
    localStorage.setItem(GlobalConstants.USER_KEY, serialized);
    if (localStorage.getItem(GlobalConstants.USER_KEY) !== serialized) { throw new Error('Authentication session could not be persisted'); }
  }

  private readLegacyOfflinePassword(user: SessionUser): string | null {
    return user.OfflinePasswordRecord === undefined ? localStorage.getItem(GlobalConstants.OFFLINE_PASSWORD_RECORD) : null;
  }

  private removeLegacyCredentials(): void {
    for (const key of [GlobalConstants.OFFLINE_PASSWORD_RECORD, GlobalConstants.ACCESS_TOKEN, GlobalConstants.IS_AUTHENTICATED]) {
      localStorage.removeItem(key);
      if (localStorage.getItem(key) !== null) { throw new Error('Authentication artifact could not be removed'); }
    }
  }

  /** Web Lock precedes IndexedDB coordination; its signal bounds acquisition, not granted work. The metadata transaction separately bounds synchronous actions. */
  private async executeWithLock<Result>(action: () => Result): Promise<Result> {
    if (this.destroyed) { throw new Error('Authentication session owner was destroyed'); }
    const run = () => this.metadata.withSessionLock(action);
    if (navigator.locks) {
      const controller = new AbortController();
      this.controllers.add(controller);
      const timer = setTimeout(() => controller.abort(), 30000);
      try {
        const outcome: { result?: { value: Result } | { error: unknown } } = {};
        await navigator.locks.request('FlexAuthenticationContext', { signal: controller.signal }, () =>
          run().then(value => { outcome.result = { value }; }, (error: unknown) => { outcome.result = { error }; }));
        const result = outcome.result;
        if (!result) { throw new Error('Authentication lock did not execute the action'); }
        if ('error' in result) { throw result.error; }
        return result.value;
      }
      finally { clearTimeout(timer); this.controllers.delete(controller); }
    }
    return run();
  }
}